<!-- #72 -->
<template>
  <v-col class="mx-4">
    <div class="d-flex">
      <div
        class="text-h6 py-0 my-0 pl-0 mr-5"
        style="color: #23468c; font-weight: 500"
      >
        Reconciliation Pending
      </div>
    </div>
    <v-divider class="mt-1 mb-4"></v-divider>
    <v-data-table
      :headers="headers"
      :items="orderList"
      :options.sync="options"
      :server-items-length="total"
      :loading="orderListLoader"
      class="elevation-1 custom-icon-size mt-7"
      :footer-props="{
        itemsPerPageOptions: [25, 50, 100, 250],
        showFirstLastPage: true,
        firstIcon: 'mdi-arrow-collapse-left',
        lastIcon: 'mdi-arrow-collapse-right',
        prevIcon: 'mdi-minus',
        nextIcon: 'mdi-plus',
      }"
    >
      <template v-slot:top>
        <v-form>
          <v-row class="custom-row pl-5 pr-5">
            <v-col cols="12" sm="3">
              <v-autocomplete
                v-model="client_name"
                :items="clients"
                outlined
                dense
                chips
                item-text="name"
                item-value="id"
                small-chips
                label="Client name"
                clearable
                @focus="fetchClients"
                @click:clear="clearInput('this.client_name')"
              ></v-autocomplete>
            </v-col>
            <v-col cols="12" sm="3">
              <v-autocomplete
                v-model="carrier"
                :items="carriers"
                outlined
                dense
                chips
                item-text="name"
                item-value="id"
                small-chips
                label="Carrier name"
                clearable
                @click:clear="clearInput('this.carrier')"
              ></v-autocomplete>
            </v-col>
            <v-col cols="12" sm="3">
              <v-autocomplete
                v-model="country"
                :items="countries"
                outlined
                dense
                chips
                item-text="name"
                item-value="id"
                small-chips
                label="Country"
                clearable
                @click:clear="clearInput('this.country')"
              ></v-autocomplete>
            </v-col>
            <v-col cols="12" sm="3">
              <v-text-field
                label="RSA Tracking #"
                v-model="rsaTrackingId"
                outlined
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="3">
              <v-text-field
                label="Carrier Tracking #"
                v-model="carrierTrackingId"
                outlined
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="3">
              <v-autocomplete
                v-model="rsaPending"
                :items="pending"
                outlined
                item-text="name"
                item-value="id"
                label="Pending From RSA"
              ></v-autocomplete>
            </v-col>
            <v-col cols="12" sm="3">
              <v-autocomplete
                v-model="carrierPending"
                :items="pending"
                outlined
                item-text="name"
                item-value="id"
                label="Pending From Carrier"
              ></v-autocomplete>
            </v-col>
            <v-col cols="12" sm="3">
              <v-menu
                v-model="dateMenu"
                :close-on-content-click="false"
                :nudge-right="40"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    :value="getFormattedDate(from_date)"
                    label="From Date"
                    append-icon="mdi-calendar"
                    readonly
                    v-bind="attrs"
                    v-on="on"
                    clearable
                    outlined
                    @click:clear="clearDateInput('this.from_date')"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="from_date"
                  @input="dateMenu = false"
                  :max="getEndDate"
                ></v-date-picker>
              </v-menu>
            </v-col>
            <v-col cols="12" sm="3">
              <v-menu
                v-model="dateMenuTo"
                :close-on-content-click="false"
                :nudge-right="40"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    :value="getFormattedDate(to_date)"
                    label="To Date"
                    append-icon="mdi-calendar"
                    readonly
                    v-bind="attrs"
                    v-on="on"
                    clearable
                    outlined
                    @click:clear="clearDateInput('this.to_date')"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="to_date"
                  @input="dateMenuTo = false"
                  :max="getEndDate"
                ></v-date-picker>
              </v-menu>
            </v-col>
            <v-col cols="9" class="text-end pr-4 mb-2">
              <v-btn
                class="mt-4 mr-4"
                color="info"
                @click="download"
                :loading="downloader"
                v-if="
                  checkPermission(
                    'app_cod_reconcile:download-cod-pending-order'
                  )
                "
                >Download</v-btn
              >
              <v-btn class="mt-4 success" color="primary" @click="searchAll"
                >Search</v-btn
              >
              <v-btn color="white" class="mt-4 ml-4" @click="allClear"
                >Clear</v-btn
              >
            </v-col>
          </v-row>
          <v-divider class="mb-2 mt-2"></v-divider>
        </v-form>
      </template>

      <template v-slot:[`item.delivered_date`]="{ item }">
        {{ item.delivered_date | localMoment | moment("DD MMM, YYYY") }}
      </template>

      <template v-slot:[`item.cod_amount`]="{ item }">
        {{ changeFormat(item.cod_amount) }}</template
      >
      <template v-slot:[`item.amount_collected_by_carrier`]="{ item }">
        {{ changeFormat(item.amount_collected_by_carrier) }}</template
      >
      <template v-slot:[`item.charges_by_carrier`]="{ item }">
        {{ changeFormat(item.charges_by_carrierP) }}</template
      >
      <template v-slot:[`item.rsa_tracking_id`]="{ item }">
        <label
          @click="fetchTrackingDetails(item.rsa_tracking_id)"
          v-if="checkPermission('app_client:tracking_details')"
        >
          <a>{{ item.rsa_tracking_id }}</a>
        </label>
        <label v-else>{{ item.rsa_tracking_id }}</label>
      </template>
    </v-data-table>
  </v-col>
</template>

<script>
import { mapActions, mapGetters } from "vuex";

export default {
  components: {},
  computed: {
    ...mapGetters(["userRole", "userCategory"]),
    getEndDate() {
      var endDate = new Date();
      endDate.setDate(endDate.getDate() + 1);
      endDate.setHours(0, 0, 0, 0); // Set hours, minutes, seconds, and milliseconds to zero
      return endDate.toISOString().split("T")[0]; // Keep only the date part
    },
  },
  data: () => ({
    clients: [],
    countries: [],
    carriers: [],
    dateMenu: false,
    from_date: "",
    to_date: "",
    dateMenuTo: false,
    rsaTrackingId: null,
    carrierTrackingId: null,
    rsaPending: "",
    carrierPending: "",
    pending: [
      { name: "Yes", id: 0 },
      { name: "No", id: 1 },
    ],
    headers: [
      {
        text: "Carrier",
        value: "carrier_id",
        class: "body-1 font-weight-bold",
        align: "left",
        sortable: false,
      },
      {
        text: "Client Name",
        value: "client_id",
        class: "body-1 font-weight-bold",
        align: "left",
        sortable: false,
      },
      {
        text: "RSA Tracking #",
        value: "rsa_tracking_id",
        class: "body-1 font-weight-bold",
        align: "left",
        sortable: false,
      },
      {
        text: "Carrier Tracking #",
        value: "carrier_tracking_id",
        class: "body-1 font-weight-bold",
        align: "left",
        sortable: false,
      },

      {
        text: "Country",
        value: "destination_country_id",
        class: "body-1 font-weight-bold",
        align: "left",
        sortable: false,
      },

      {
        text: "Collectable Amount",
        value: "cod_amount",
        class: "body-1 font-weight-bold",
        align: "left",
        sortable: false,
      },
      {
        text: "Amount collected by carrier",
        value: "amount_collected_by_carrier",
        class: "body-1 font-weight-bold",
        align: "left",
        sortable: false,
      },
      {
        text: "Charges by carrier",
        value: "charges_by_carrier",
        class: "body-1 font-weight-bold",
        align: "left",
        sortable: false,
      },
      {
        text: "Currency",
        value: "cod_currency_code",
        class: "body-1 font-weight-bold",
        align: "left",
        sortable: false,
      },
      {
        text: "Delivered date",
        value: "delivered_date",
        class: "body-1 font-weight-bold",
        align: "left",
        sortable: false,
      },
    ],
    options: {},
    total: 0,
    orderListLoader: false,
    filtersArr: [],
    orderList: [],
    client_name: null,
    carrier: null,
    country: null,
    downloader: false,
  }),

  watch: {
    options: {
      handler() {
        this.fetchOrderList();
      },
      deep: true,
    },
  },
  created() {
    this.fetchClients();
    this.fetchCarriers();
    this.getCountryList();
  },
  methods: {
    ...mapActions([
      "pendingCodOrderList",
      "fetchClient",
      "getCarriersDropdown",
      "getCountries",
      "downloadReconciliationList",
    ]),
    fetchTrackingDetails(item) {
      window.open(
        this.$router.resolve({
          name: "ClientTrackingDetails",
          query: {
            id: item,
          },
        }).href,
        "_blank"
      );
    },
    getCountryList() {
      this.getCountries().then((res) => {
        this.countries = res.data;
      });
    },
    fetchCarriers() {
      this.getCarriersDropdown({
        pagination: 0,
        active: "all",
      })
        .then((res) => {
          this.carriers = res.data;
        })
        .catch(() => {
          this.loader = false;
        });
    },
    fetchClients() {
      if (this.clients.length == 0)
        this.fetchClient({
          service: "clickdna,fulfillment_wid_last_mile",
        }).then((res) => {
          this.clients = res.data;
        });
    },
    clearInput(val) {
      if (val) {
        val = null;
      }
    },
    clearDateInput(type) {
      if (type == "this.to_date") {
        this.to_date = "";
      } else {
        this.from_date = "";
      }
    },
    async download() {
      this.downloader = true;
      await this.allFilters();
      this.downloadReconciliationList({ ...this.filtersArr })
        .then((res) => {
          this.fileDownload(res);
          this.downloader = false;
        })
        .catch(() => {
          this.downloader = false;
        });
    },
    allFilters() {
      this.loader = true;
      let obj = {
        from_date: this.from_date,
        to_date: this.to_date,
        carrier_id: this.carrier,
        client_id: this.client_name,
        destination_country_id: this.country,
        rsa_tracking_id: this.rsaTrackingId,
        carrier_tracking_id: this.carrierTrackingId,
        pending_rsa: this.rsaPending,
        pending_carrier: this.carrierPending,
      };
      Object.keys(obj).forEach((key) => {
        if (obj[key] === null || obj[key] === undefined || obj[key] === "") {
          delete obj[key];
        }
      });
      this.filtersArr = obj;
    },
    searchAll() {
      this.options.page = 1;
      this.allFilters();
      this.pendingCodOrderList({
        ...this.options,
        ...this.filtersArr,
        ...this.$route.params,
        type: this.$route.params.type,
      })
        .then((res) => {
          this.orderListLoader = false;
          this.orderList = res.data.records;
          this.total = res.data.total;
        })
        .catch(() => {
          this.loader = false;
        });
    },
    clear() {
      this.to_date = null;
      this.from_date = null;
      this.carrier = "";
      this.client_name = "";
      this.country = "";
      this.rsaTrackingId = "";
      this.carrierTrackingId = "";
      this.rsaPending = "";
      this.carrierPending = "";
    },
    async allClear() {
      await this.clear();
      this.filtersArr = {};
      await this.fetchOrderList();
    },

    fetchOrderList() {
      this.orderList = [];
      this.orderListLoader = true;
      this.pendingCodOrderList({
        ...this.options,
        ...this.filtersArr,
        ...this.$route.params,
      })
        .then((res) => {
          this.orderListLoader = false;
          this.orderList = res.data.records;
          this.total = res.data.total;
        })
        .catch(() => {
          this.orderListLoader = false;
        });
    },
  },
};
</script>
